// Pure helpers for the patient-portal Shipment History table on /me/history.
//
// IMPORTANT: this module is intentionally pure — no JSX, no React hooks — so the
// shipment-row derivation (which fill requests count as a shipment, how each is
// badged, and which address it was delivered to) can be unit-tested in isolation.
// The portalShipmentHistory.jsx component maps these rows to JSX.

import {
  FILL_REQUEST_DEAD_STATUSES,
  fillRequestMedications,
  shippingAddressForVisit,
} from "~/patient-portal/utils/treatmentCard";

import { isPharmacyPickupOrderType } from "~/shared/utils/sharedUtils";

import {
  verticalBadgeColorForVisit,
  verticalLabelForVisit,
} from "~/patient-portal/utils/visitGrouping";

export const SHIPMENT_BADGE_REFILL = "Refill";

// Carrier slug (as stored by AfterShip) → display label.
const CARRIER_DISPLAY = {
  usps: "USPS",
  fedex: "FedEx",
  ups: "UPS",
  dhl: "DHL",
};

export function carrierDisplayName(slug) {
  if (!slug) return "";
  return CARRIER_DISPLAY[slug.toLowerCase()] || slug.toUpperCase();
}

// A refill — including an autoship cycle, which ships as a refill — reads "Refill".
// An original order gets no badge.
export function shipmentBadge(fr) {
  if (fr.isRefill) return SHIPMENT_BADGE_REFILL;
  return "";
}

// Go's zero time ("0001-01-01...") marshals to year <= 1; treat that as "unset".
function isRealDate(value) {
  if (!value) return false;
  const d = new Date(value);
  return !isNaN(d.getTime()) && d.getFullYear() > 1;
}

// A fill request is a real mail shipment to the patient once it carries a carrier
// tracking number (mail orders get AfterShip tracking; pharmacy-pickup eRx does
// not), or — for a mail-order visit — once the pharmacy has marked it filled
// (completedAt). Failed/cancelled fill requests never count.
export function isMailShipment(visit, fr) {
  if (!fr) return false;
  if (FILL_REQUEST_DEAD_STATUSES.includes(fr.status)) return false;
  if (fr.trackingNumber) return true;
  return (
    !isPharmacyPickupOrderType(visit.orderType) && isRealDate(fr.completedAt)
  );
}

// The address a shipment was delivered to. Fill requests now persist the
// address they were actually sent with (set by an EMR staff edit or snapshotted
// at send time), so a fill's own address is authoritative when present. Legacy
// rows (empty address1) fall back to the ship-to captured on the order payment
// (VOP) that triggered THIS fill request (a refill can ship elsewhere), then to
// the visit's latest address when that VOP has none.
function shipmentAddress(visit, fr, vopById) {
  if (fr.address1) {
    return {
      address1: fr.address1,
      address2: fr.address2,
      city: fr.city,
      usState: fr.usState,
      zip: fr.zip,
    };
  }
  const vop = vopById[fr.visitOrderPaymentID];
  if (vop && vop.address1) {
    return {
      address1: vop.address1,
      address2: vop.address2,
      city: vop.city,
      usState: vop.usState,
      zip: vop.zip,
    };
  }
  return shippingAddressForVisit(visit);
}

// Builds the flat, newest-first list of shipment rows across all of a patient's
// visits. Each row: { key, visit, fr, meds, clinic, badge, method, address,
// dateSent }. `meds` are prescription objects — resolve their display string
// with medicationLabel in the component.
export function buildShipmentRows(visits) {
  const rows = [];
  (visits || []).forEach((visit) => {
    const vopById = {};
    (visit.visitOrderPayments || []).forEach((vop) => {
      if (vop) vopById[vop.id] = vop;
    });
    (visit.fillRequests || []).forEach((fr) => {
      if (!isMailShipment(visit, fr)) return;
      const dateSent = isRealDate(fr.completedAt)
        ? fr.completedAt
        : fr.createdAt;
      rows.push({
        key: fr.id || `${visit.id}:${dateSent}`,
        visit,
        fr,
        meds: fillRequestMedications(visit, fr),
        clinic: verticalLabelForVisit(visit),
        clinicColor: verticalBadgeColorForVisit(visit),
        badge: shipmentBadge(fr),
        method: carrierDisplayName(fr.shipmentMethod),
        address: shipmentAddress(visit, fr, vopById),
        dateSent,
      });
    });
  });
  return rows.sort((a, b) => new Date(b.dateSent) - new Date(a.dateSent));
}
